/* eslint-disable max-lines -- TODO: extract bulk-download wiring into a smaller hook or sub-component */
/* eslint-disable max-lines-per-function -- TODO: extract bulk-download wiring into a smaller hook or sub-component */
/* eslint-disable complexity -- TODO: extract bulk-download wiring into a smaller hook or sub-component */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { BulkDownloadManifestResponse, OfferPriceOverrideFieldId } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { useScrollSentinel } from '@ooh/ui/components/scroll-sentinel';
import { StickyHorizontalScrollbar } from '@ooh/ui/components/sticky-horizontal-scrollbar';
import { useMeasuredElement } from '@ooh/ui/components/use-measured-element';
import { TooltipProvider } from '@ooh/ui/components/tooltip';
import { toast } from 'sonner';
import { buildMediaTypeOptionLabels } from '../../../lib/media-type-options';
import {
  useOfferUnits,
  useOfferUnitsAll,
  useOfferUnitFilterOptions,
  useOfferUnitsGrandTotal,
  useUpdateOfferUnit,
  useRemoveOfferUnits,
} from './api';
import { OfferConflictError } from './api';
import {
  useBatchUpdatePrices,
  OfferConflictError as PricingOfferConflictError,
} from '../pricing-tab/api';
import { PricingTabTotalsFooter } from '../pricing-tab/pricing-tab-totals-footer';
import { BulkActionsOverlay } from './bulk-actions-overlay';
import {
  buildUnitsTabColumns,
  getOfferTableCatalogColumnIds,
  seedOfferTableColumnPreferencesFromCatalog,
  useOfferTableColumnPreferences,
} from './units-tab-columns';
import { BulkDiscountPopover } from './bulk-discount-popover';
import { BulkDiscountConfirmDialog } from './bulk-discount-confirm-dialog';
import {
  OFFER_TABLE_DEFAULT_CURRENCY,
  type OfferPriceFieldId,
} from './lib/offer-unit-price-columns';
import { useOfferTableAvailability } from './hooks/use-offer-table-availability';
import { UnifiedSelectPopover } from '@ooh/screens';
// Cross-feature import of the catalog's own availability sub-toolbar + legend-item
// builder — an existing precedent (see `docs/WEB_ARCHITECTURE.md:36` convention-gap
// finding; units-tab-columns.tsx's own CATALOG_DEFAULT_HIDDEN_COLUMNS import is
// another precedent in this same feature), needed here so the merged table's slim
// availability strip matches the catalog's chrome exactly instead of duplicating it.
import { AvailabilitySubToolbar, buildAvailabilityLegendItems } from '@ooh/screens';
import { UnitsTabToolbar } from './units-tab-toolbar';
import type { SortDir, SortKey } from './units-tab-toolbar';
import type { OfferUnitItem } from './units-tab-columns';
import {
  OfferUnitsEmptyState,
  OfferUnitsNoResultsState,
  UnitsTableContent,
} from './units-tab-page-parts';
import { UnitsTabMap } from './units-tab-map';
import { OfferBulkDownloadTrigger } from '../offer-bulk-download-trigger';
import { mapSortKeyToServer, mapFilterValueToServerFilters } from './lib/server-query-params';
import {
  buildOfferUnitFilterFields,
  buildLocationFilterField,
  buildLocationPanelValue,
  adaptLocationFilterChange,
  splitFilterPanelChange,
} from './lib/filter-fields';
import { buildOrderedVisibleColumns } from '../../../lib/ordered-visible-columns';
import { useUnitsTableStickyHeaderOffset } from './hooks/use-units-table-sticky-header-offset';

/**
 * A retired address's `?tab=pricing`/`?tab=availability` value, threaded down
 * from the route redirect so the addressed columns reveal themselves for
 * that visit only, without altering the user's saved layout
 * (offer-unified-table: "A retired address reveals the columns it was saved
 * for"). `'availability'` has no visible effect until task 4.2 adds
 * availability columns — accepted now so the redirect/reveal plumbing needs
 * no second migration.
 */
export type OfferTableRevealSurface = 'pricing' | 'availability';

const REVEALED_PRICE_COLUMN_IDS = ['clientPriceNet', 'discountPercent', 'lineTotal'];

/**
 * A `PUT /offers/:offerId/prices` row as built by this file's price-mutation
 * handlers. Every field here follows the same three-wire-state partial-
 * update rule (`packages/db`'s batch-upsert-prices.ts): a key OMITTED from
 * the row leaves that column untouched; an explicit `null` clears it back to
 * "unpriced" (`clientPriceNet`), "no discount" (`discountPercent`), or
 * "inherit from the media unit" (the nine formatka override fields); a
 * number sets it. `clientPriceNet`/`discountPercent` therefore accept `null`
 * too — an earlier revision of this type omitted `null` for them, which let
 * `handlePriceChange` silently drop a cleared price/discount instead of
 * persisting the clear (CU-86cbgnt38 QA fix).
 */
type PriceMutationRow = {
  unitId: OfferUnitItem['id'];
  currency: string;
} & Partial<Record<'clientPriceNet' | 'discountPercent', number | null>> &
  Partial<Record<OfferPriceOverrideFieldId, number | null>>;

interface UnitsTabPageProps {
  offerId: number;
  canEdit: boolean;
  filterValue?: UnitsTabFilterValue;
  onFilterChange?: (filterValue: UnitsTabFilterValue) => void;
  /** Free-text multi-column search term (catalog parity). URL-owned by the route. */
  search?: string;
  onSearchChange?: (search: string) => void;
  /**
   * Dedicated address text filter (catalog parity, CU-86ca9k73a), distinct
   * from the multi-column `search` above. URL-owned by the route (scalar
   * sidecar, same pattern as `search`).
   */
  address?: string;
  onAddressChange?: (address: string) => void;
  /**
   * Numeric range dimensions (CU-86ca9k73a): `Record<id, [min, max]>`,
   * URL-owned by the route via the neutral `useRangeFilterParams` hook
   * (`src/hooks/use-range-filter-params.ts`). Threaded as a prop (not called
   * directly inside this component) so this component stays renderable
   * without a `RouterProvider` in unit tests — see the route-level
   * `readRangeFilterValues`/`buildNextRangeFilterValues` wiring in
   * `routes/offers/$offerId/index.tsx`.
   */
  rangeValue?: Record<string, string[]>;
  onRangeChange?: (rangeValue: Record<string, string[]>) => void;
  /**
   * Resolved at route level (`cost.view` permission) — omits internal
   * cost-gated range filter dimensions when false. See
   * `WEB_ARCHITECTURE.md`'s "resolve permission booleans in feature-level
   * parents" rule; catalog parity (`routes/media-units/index.tsx`).
   */
  canViewCosts?: boolean;
  /**
   * Resolved at route level (wildcard `cost.view_rent` grant) — omits the
   * rent range filter specifically, on top of `canViewCosts`
   * (field-visibility, CU-86cbgnt3z rent-visibility split).
   */
  canViewRent?: boolean;
  /**
   * Resolved at route level (`supplier.view` permission) — omits the three
   * supplier-contact filter fields when false (catalog-column-filter-parity).
   */
  canViewSupplierDetails?: boolean;
  offerUpdatedAt: Date;
  onSortChange?: (sortKey: SortKey, sortDir: SortDir) => void;
  sortKey?: SortKey;
  sortBy?: SortKey;
  sortDir?: SortDir;
  decisionVersionId?: number;
  /**
   * Callback wired at route level from useBulkDownloadSpecs().start().
   * When provided, a bulk-download trigger is rendered in the toolbar area.
   * Receives all offer unit IDs — row-level selection is deliberately ignored (D-7).
   */
  onBulkDownloadSpecs?: (ids: number[]) => void;
  /**
   * pendingManifest from useBulkDownloadSpecs() — null when not awaiting confirm.
   * Passed down so the route can render the confirm dialog.
   */
  bulkDownloadPendingManifest?: BulkDownloadManifestResponse | null;
  confirmBulkDownload?: () => Promise<void>;
  cancelBulkDownload?: () => void;
  /** Retired-address one-visit column reveal — see `OfferTableRevealSurface`. */
  revealSurface?: OfferTableRevealSurface;
  /** Visible offer/client/brand identity summary for the availability queue-join panel. */
  offerLabel?: string;
  /** Offer's own campaign dates — `null` explains the missing dates instead of rendering columns. */
  defaultDateFrom?: Date | null;
  defaultDateTo?: Date | null;
  /** Opens the offer header's campaign-dates editor drawer, from the no-dates CTA. */
  onOpenCampaignDatesEditor?: () => void;
}

// `supplierEmail`/`supplierPhone`/`supplierContactInfo` were removed
// entirely (CU-86cb1nq7h, client request) — see
// `features/offers/units-tab/lib/filter-fields.ts` doc comments.
// `supplierNumber` stays (client-confirmed key identifier, not contact info).
export type UnitsTabFilterValue = Record<
  | 'region'
  | 'county'
  | 'agglomeration'
  | 'city'
  | 'format'
  | 'mediaType'
  | 'supplier'
  | 'lighting'
  | 'supplierNumber'
  | 'grwId'
  // ─── Catalog-parity array-valued filters (CU-86ca9k73a) ───
  | 'material'
  | 'multiformat'
  | 'tags'
  // ─── Column-parity filters (catalog-column-filter-parity) — text fields
  // ride the array model as a one-element array (unwrapped to a scalar
  // server filter param in mapFilterValueToServerFilters). `status` was
  // removed entirely (CU-86cb1nq7h follow-up) — redundant with detailed
  // availability. ───
  | 'notes'
  | 'roadNumber',
  string[]
>;

export const EMPTY_FILTER_VALUE: UnitsTabFilterValue = {
  region: [],
  county: [],
  agglomeration: [],
  city: [],
  format: [],
  mediaType: [],
  supplier: [],
  lighting: [],
  supplierNumber: [],
  grwId: [],
  material: [],
  multiformat: [],
  tags: [],
  notes: [],
  roadNumber: [],
};

function useUnitsTabFilters(
  offerId: number,
  filterValue: UnitsTabFilterValue,
  onFilterChange: (filterValue: UnitsTabFilterValue) => void,
  address: string,
  onAddressChange: (address: string) => void,
  rangeValue: Record<string, string[]>,
  onRangeChange: (value: Record<string, string[]>) => void,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
  tMap: ReturnType<typeof useTranslation<'map'>>['t'],
  canViewCosts: boolean,
  canViewRent: boolean,
  canViewSupplierDetails: boolean,
) {
  const { data: filterOptions } = useOfferUnitFilterOptions(offerId);

  const filterFieldLabels = React.useMemo(
    () => ({
      region: t('workspace.unitsTab.filterFields.region'),
      county: t('workspace.unitsTab.filterFields.county'),
      agglomeration: t('workspace.unitsTab.filterFields.agglomeration'),
      city: t('workspace.unitsTab.filterFields.city'),
      format: t('workspace.unitsTab.columns.format'),
      mediaType: t('workspace.unitsTab.filterFields.mediaType'),
      // Reuses the shared `src/lib/media-type-options.ts` helper (catalog
      // parity — see features/media-units/hooks/use-filter-labels.ts).
      mediaTypeOptionLabels: buildMediaTypeOptionLabels(tMap),
      supplier: t('workspace.unitsTab.columns.supplier'),
      lighting: t('workspace.unitsTab.filterFields.lighting'),
      supplierNumber: t('workspace.unitsTab.filterFields.supplierNumber'),
      grwId: t('workspace.unitsTab.filterFields.grwId'),
      // Combined "Lokalizacja" field — see buildLocationFilterField.
      location: t('workspace.unitsTab.columns.location'),
      locationSearchPlaceholder: t('workspace.unitsTab.filterFields.locationSearchPlaceholder'),
      locationInfoHint: t('workspace.unitsTab.filterFields.locationInfoHint'),
      locationInfoHintLabel: t('workspace.unitsTab.filterFields.locationInfoHintLabel'),
      searchPlaceholder: t('workspace.unitsTab.filterFields.searchPlaceholder'),
      // ─── Catalog-parity filter labels (CU-86ca9k73a) ───
      material: t('workspace.unitsTab.filterFields.material'),
      multiformat: t('workspace.unitsTab.filterFields.multiformat'),
      tags: t('workspace.unitsTab.filterFields.tags'),
      address: t('workspace.unitsTab.filterFields.address'),
      area: t('workspace.unitsTab.filterFields.area'),
      width: t('workspace.unitsTab.filterFields.width'),
      height: t('workspace.unitsTab.filterFields.height'),
      audience: t('workspace.unitsTab.filterFields.audience'),
      catalogPrice: t('workspace.unitsTab.filterFields.catalogPrice'),
      rent: t('workspace.unitsTab.filterFields.rent'),
      exposureCost: t('workspace.unitsTab.filterFields.exposureCost'),
      additionalCosts: t('workspace.unitsTab.filterFields.additionalCosts'),
      discountedPrice: t('workspace.unitsTab.filterFields.discountedPrice'),
      lastMinutePrice: t('workspace.unitsTab.filterFields.lastMinutePrice'),
      starterPrice: t('workspace.unitsTab.filterFields.starterPrice'),
      productionCost: t('workspace.unitsTab.filterFields.productionCost'),
      installationCost: t('workspace.unitsTab.filterFields.installationCost'),
      dismantlingCost: t('workspace.unitsTab.filterFields.dismantlingCost'),
      clientProduction: t('workspace.unitsTab.filterFields.clientProduction'),
      clientInstallation: t('workspace.unitsTab.filterFields.clientInstallation'),
      clientDismantling: t('workspace.unitsTab.filterFields.clientDismantling'),
      clientAdditionalFees: t('workspace.unitsTab.filterFields.clientAdditionalFees'),
      // ─── Column-parity filter labels (catalog-column-filter-parity) ───
      notes: t('workspace.unitsTab.filterFields.notes'),
      roadNumber: t('workspace.unitsTab.filterFields.roadNumber'),
    }),
    [t, tMap],
  );

  const filterFields = React.useMemo(
    () =>
      buildOfferUnitFilterFields(
        filterOptions,
        filterFieldLabels,
        canViewCosts,
        canViewSupplierDetails,
        canViewRent,
      ),
    [filterOptions, filterFieldLabels, canViewCosts, canViewSupplierDetails, canViewRent],
  );

  // Combined "Lokalizacja" pill: merges region/county/agglomeration/city into
  // one flat namespaced `location` value for FilterPanel display, and buckets
  // any update on it back into those four real keys before reaching
  // `onFilterChange` (catalog parity — see units-tab/lib/filter-fields.ts).
  const locationField = React.useMemo(
    () => buildLocationFilterField(filterOptions, filterFieldLabels),
    [filterOptions, filterFieldLabels],
  );
  // FilterPanel's value model is one flat `string[]` per field id — merge in
  // the address sidecar (single-element array, catalog parity) and every
  // active range dimension's `[min, max]` pair alongside `filterValue` +
  // the combined location pill.
  const panelFilterValue = React.useMemo(
    () => ({
      ...filterValue,
      location: buildLocationPanelValue(filterValue),
      address: address ? [address] : [],
      ...rangeValue,
    }),
    [filterValue, address, rangeValue],
  );
  const handleLocationAdaptedChange = React.useMemo(
    () =>
      adaptLocationFilterChange(
        locationField,
        onFilterChange as (value: Record<string, string[]>) => void,
      ),
    [locationField, onFilterChange],
  );
  // Splits FilterPanel's single combined `onChange` value into its three
  // destinations: range dimensions -> onRangeChange (scalar sidecar), address
  // -> onAddressChange (scalar sidecar), everything else (incl. "location",
  // bucketed back to region/county/agglomeration/city) -> onFilterChange.
  const handlePanelFiltersChange = React.useCallback(
    (nextValue: Record<string, string[]>) => {
      const { rangeSubset, address: nextAddress, rest } = splitFilterPanelChange(nextValue);
      if (Object.keys(rangeSubset).length > 0) onRangeChange(rangeSubset);
      if (nextAddress !== undefined) onAddressChange(nextAddress);
      handleLocationAdaptedChange(rest);
    },
    [handleLocationAdaptedChange, onRangeChange, onAddressChange],
  );

  const filterPanelLabels = React.useMemo(
    () => ({
      allLabel: t('common:filters.allLabel', { defaultValue: 'Wszystkie' }),
      searchPlaceholder: t('common:filters.searchPlaceholder', { defaultValue: 'Szukaj...' }),
      getSelectedCountLabel: (count: number) =>
        t('common:filters.selectedCountLabel', { defaultValue: '{{n}} wybranych', n: count }),
      getClearAriaLabel: (fieldId: string) =>
        t('common:filters.clearLabel', { defaultValue: 'Wyczysc' }) + ` ${fieldId}`,
      clearAllLabel: t('common:filters.clearAllLabel', { defaultValue: 'Wyczysc wszystko' }),
      addFilterLabel: t('common:filters.addFilterLabel', { defaultValue: 'Dodaj filtr' }),
      rangeLabels: {
        minLabel: t('workspace.unitsTab.filterFields.rangeMinLabel'),
        maxLabel: t('workspace.unitsTab.filterFields.rangeMaxLabel'),
        applyLabel: t('common:filters.applyLabel', { defaultValue: 'Zastosuj' }),
        clearLabel: t('common:filters.clearLabel', { defaultValue: 'Wyczysc' }),
      },
    }),
    [t],
  );

  return {
    filterFields,
    filterPanelLabels,
    panelFilterValue,
    handlePanelFiltersChange,
  };
}

function renderUnitsTabContent({
  canEdit,
  displayItems,
  columns,
  onColumnReorder,
  onColumnResizeEnd,
  sortBy,
  sortDir,
  onSort,
  onOpenMapMode,
  onPrimaryAction,
  offerId,
  hasActiveFilter,
  unfilteredTotal,
  onClearFilters,
  onScrollContainerElement,
  stickyHeaderOffsetPx,
  t,
}: {
  canEdit: boolean;
  columns: ReturnType<typeof buildUnitsTabColumns>;
  displayItems: OfferUnitItem[];
  onColumnReorder: (fromId: string, toId: string) => void;
  onColumnResizeEnd: (columnId: string, widthPx: number) => void;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: string) => void;
  onOpenMapMode?: () => void;
  onPrimaryAction: () => void;
  offerId: string;
  hasActiveFilter: boolean;
  unfilteredTotal: number;
  onClearFilters: () => void;
  onScrollContainerElement?: (element: HTMLDivElement | null) => void;
  stickyHeaderOffsetPx: number;
  t: ReturnType<typeof useTranslation<'offers'>>['t'];
}) {
  if (displayItems.length === 0 && hasActiveFilter) {
    // A filter (or search) matching nothing is NOT the empty-offer state —
    // offer-unified-table: "A filter matching nothing is not the empty-offer
    // state".
    return (
      <OfferUnitsNoResultsState
        unfilteredTotal={unfilteredTotal}
        onClearFilters={onClearFilters}
        t={t}
      />
    );
  }

  if (displayItems.length === 0) {
    return (
      <OfferUnitsEmptyState
        canEdit={canEdit}
        onOpenMapMode={onOpenMapMode}
        onPrimaryAction={onPrimaryAction}
        t={t}
      />
    );
  }

  return (
    <UnitsTableContent
      columns={columns}
      displayItems={displayItems}
      onColumnReorder={onColumnReorder}
      onColumnResizeEnd={onColumnResizeEnd}
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
      offerId={offerId}
      onScrollContainerElement={onScrollContainerElement}
      stickyHeaderOffsetPx={stickyHeaderOffsetPx}
      t={t}
    />
  );
}

export function UnitsTabPage({
  offerId,
  canEdit,
  filterValue = EMPTY_FILTER_VALUE,
  onFilterChange = () => {},
  search = '',
  onSearchChange = () => {},
  address = '',
  onAddressChange = () => {},
  rangeValue = {},
  onRangeChange = () => {},
  canViewCosts = true,
  canViewRent = true,
  canViewSupplierDetails = true,
  offerUpdatedAt,
  onSortChange,
  sortKey = 'sortOrder',
  sortBy,
  sortDir = 'asc',
  decisionVersionId,
  onBulkDownloadSpecs,
  bulkDownloadPendingManifest = null,
  confirmBulkDownload,
  cancelBulkDownload,
  revealSurface,
  offerLabel,
  defaultDateFrom = null,
  defaultDateTo = null,
  onOpenCampaignDatesEditor,
}: UnitsTabPageProps) {
  const { t } = useTranslation('offers');
  const { t: tMap } = useTranslation('map');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const activeSortKey = sortBy ?? sortKey;
  const serverSort = React.useMemo(
    () => mapSortKeyToServer(activeSortKey, sortDir),
    [activeSortKey, sortDir],
  );
  const serverFilters = React.useMemo(
    () => mapFilterValueToServerFilters(filterValue, search, address, rangeValue),
    [filterValue, search, address, rangeValue],
  );
  const {
    allRows,
    totalCount,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useOfferUnits(offerId, {
    sort: serverSort,
    filters: serverFilters,
    decisionVersionId,
  });
  const updateUnit = useUpdateOfferUnit(offerId);
  const removeUnits = useRemoveOfferUnits(offerId);
  const batchUpdatePrices = useBatchUpdatePrices(offerId);
  const [tableScrollElement, setTableScrollElement] = React.useState<HTMLDivElement | null>(null);
  // The totals footer renders nothing while loading and on no-data, so its
  // height is measured rather than assumed — it drives how far above the bottom
  // the horizontal scrollbar sits.
  const [footerHeight, footerRef] = useMeasuredElement<HTMLDivElement>(
    (node) => node.getBoundingClientRect().height,
  );
  const { toolbarRef, stickyHeaderOffsetPx: tableStickyHeaderOffsetPx } =
    useUnitsTableStickyHeaderOffset();

  const [selectedIds, setSelectedIds] = React.useState<Set<OfferUnitItem['id']>>(new Set());
  const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false);
  const [isMapVisible, setIsMapVisible] = React.useState(false);
  // Local price/discount overrides for instant client-side feedback before
  // server confirmation — see `handlePriceChange` below.
  const [priceOverrides, setPriceOverrides] = React.useState<Map<string, number | null>>(
    () => new Map(),
  );
  // Discount percent awaiting the "apply to whole offer" confirmation dialog.
  const [pendingConfirmAllDiscount, setPendingConfirmAllDiscount] = React.useState<number | null>(
    null,
  );

  // `serverFilters` is undefined exactly when no filter is active, so it
  // doubles as the active-filter signal for the footer grand-total fetch below.
  const hasActiveFilter = serverFilters !== undefined;

  // Whole-offer affordances (map overlay + bulk download + the "apply
  // discount to everyone" confirm dialog) must cover EVERY unit of the
  // offer, ignoring the active table filter and the loaded-page boundary.
  // The fetch is enabled only when one of those affordances is in play
  // (perf — offers can hold thousands of units).
  const isBulkDownloadAvailable = onBulkDownloadSpecs != null;
  const allUnitsQuery = useOfferUnitsAll(offerId, {
    // Also enabled whenever a selection exists, so a bulk discount or removal
    // submitted with hidden selected units (a filter hides them) can still
    // resolve and act on every one of them, not just the loaded page
    // (row-selection spec).
    enabled:
      isMapVisible ||
      isBulkDownloadAvailable ||
      pendingConfirmAllDiscount !== null ||
      selectedIds.size > 0,
  });
  const allUnits = React.useMemo(() => allUnitsQuery.data ?? [], [allUnitsQuery.data]);

  // Footer grand total: when a filter narrows the view, `totalCount` above is
  // the filtered count, not the offer's true total. Fetch the cheap grand
  // total (single first page, no filters — see `offerUnitQueries.grandTotal`)
  // only when a filter is active; when unfiltered, `totalCount` already IS the
  // grand total and a second request would be redundant.
  const grandTotalQuery = useOfferUnitsGrandTotal(offerId, { enabled: hasActiveFilter });

  const handleMapUnitClick = React.useCallback(
    (mediaUnitId: number) => {
      void navigate({ to: '/media-units/$id', params: { id: String(mediaUnitId) } });
    },
    [navigate],
  );

  // Add-units entry points navigate into the real catalog/map screens in offer
  // context (addToOfferId) instead of opening the retired bespoke drawer/overlay.
  const handleAddUnitsFromCatalog = React.useCallback(() => {
    void navigate({ to: '/media-units', search: { addToOfferId: String(offerId) } });
  }, [navigate, offerId]);

  const handleAddUnitsFromMap = React.useCallback(() => {
    void navigate({ to: '/map', search: { addToOfferId: String(offerId) } });
  }, [navigate, offerId]);

  const onCheckedChange = React.useCallback((id: OfferUnitItem['id'], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clearSelection = React.useCallback(() => setSelectedIds(new Set()), []);

  const confirmRemove = React.useCallback(() => {
    setRemoveConfirmOpen(false);
    removeUnits.mutate(
      { unitIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          clearSelection();
          toast.success(t('workspace.unitsTab.toasts.removeSuccess'));
        },
      },
    );
  }, [clearSelection, removeUnits, selectedIds, t]);

  const onNoteBlur = React.useCallback(
    (unitId: OfferUnitItem['id'], note: string) => {
      const unit = allRows.find((u) => u.id === unitId);
      if (!unit) return;
      const currentNote = unit.note ?? '';
      if (note === currentNote) return;

      updateUnit.mutate(
        { unitId, note, updatedAt: offerUpdatedAt },
        {
          onError: (error) => {
            if (error instanceof OfferConflictError) {
              toast.error(t('workspace.unitsTab.toasts.noteConflict'));
              return;
            }

            toast.error(t('workspace.unitsTab.toasts.noteSaveError'));
          },
        },
      );
    },
    [allRows, updateUnit, offerUpdatedAt, t],
  );

  // Refs so price-edit callbacks always read the latest query data/updatedAt
  // even when captured inside a memoised `columns` closure computed before a
  // later render's data landed (mirrors the retired pricing tab's own pattern).
  const allRowsRef = React.useRef(allRows);
  allRowsRef.current = allRows;
  const offerUpdatedAtRef = React.useRef(offerUpdatedAt);
  offerUpdatedAtRef.current = offerUpdatedAt;

  const handlePriceChange = React.useCallback(
    (unitId: OfferUnitItem['id'], field: OfferPriceFieldId, value: number | null) => {
      setPriceOverrides((prev) => {
        const next = new Map(prev);
        next.set(`${unitId}:${field}`, value);
        return next;
      });

      const row = allRowsRef.current.find((r) => r.id === unitId);
      if (!row) return;

      // `packages/db`'s batch-upsert-prices.ts does a true per-field partial
      // update for every field on the row: a field OMITTED from the row is
      // left untouched, and only an EXPLICIT `null` clears it. So the field
      // the user just edited is ALWAYS sent — as `value`, whether that is a
      // number or an explicit `null` from clearing the input — never
      // conditionally omitted, or a clear would silently not persist
      // (CU-86cbgnt38 QA fix). For a formatka override field we send ONLY
      // that field, never the other eight, never clientPriceNet/discountPercent.
      //
      // clientPriceNet/discountPercent additionally resend each other (their
      // sibling's current value) when editing one, so a field that already
      // holds a real value is never left to the — also correct, but
      // needlessly indirect — omit-means-untouched path.
      const mutationRow: PriceMutationRow = {
        unitId,
        currency: row.currency ?? OFFER_TABLE_DEFAULT_CURRENCY,
      };
      if (field === 'clientPriceNet' || field === 'discountPercent') {
        const currentPrice =
          priceOverrides.get(`${unitId}:clientPriceNet`) ?? row.clientPriceNet ?? null;
        const currentDiscount =
          priceOverrides.get(`${unitId}:discountPercent`) ?? row.discountPercent ?? null;
        if (field === 'clientPriceNet') {
          mutationRow.clientPriceNet = value;
          if (currentDiscount !== null) mutationRow.discountPercent = currentDiscount;
        } else {
          mutationRow.discountPercent = value;
          if (currentPrice !== null) mutationRow.clientPriceNet = currentPrice;
        }
      } else {
        mutationRow[field] = value;
      }

      const toastId = `offer-table-price-save-${unitId}`;

      batchUpdatePrices.mutate(
        { rows: [mutationRow], updatedAt: offerUpdatedAtRef.current },
        {
          onSuccess: () => toast.dismiss(toastId),
          onError: (error) => {
            setPriceOverrides((prev) => {
              const next = new Map(prev);
              next.delete(`${unitId}:${field}`);
              return next;
            });

            if (error instanceof PricingOfferConflictError) {
              toast.error(t('workspace.pricingTab.toasts.conflict'), { id: toastId });
              return;
            }
            toast.error(t('workspace.pricingTab.toasts.saveError'), { id: toastId });
          },
        },
      );
    },
    [batchUpdatePrices, priceOverrides, t],
  );

  const handleApplyDiscount = React.useCallback(
    (rows: OfferUnitItem[], discountPercent: number) => {
      // Only writes discountPercent (and resends clientPriceNet, its
      // full-replace-per-row sibling — unchanged, pre-existing behavior).
      // The nine formatka override fields are never included: batch-upsert-
      // prices.ts's per-field partial update already leaves a field omitted
      // from the row untouched, so a bulk discount never needs to resend
      // them to avoid clearing them.
      const mutationRows = rows.map((row) => {
        const mutationRow: {
          unitId: OfferUnitItem['id'];
          currency: string;
          discountPercent: number;
          clientPriceNet?: number;
        } = {
          unitId: row.id,
          currency: row.currency ?? OFFER_TABLE_DEFAULT_CURRENCY,
          discountPercent,
        };
        if (row.clientPriceNet != null) mutationRow.clientPriceNet = row.clientPriceNet;
        return mutationRow;
      });

      batchUpdatePrices.mutate(
        { rows: mutationRows, updatedAt: offerUpdatedAtRef.current },
        {
          onError: (error) => {
            if (error instanceof PricingOfferConflictError) {
              toast.error(t('workspace.pricingTab.toasts.conflict'));
              return;
            }
            toast.error(t('workspace.pricingTab.toasts.saveError'));
          },
          onSuccess: () => {
            setSelectedIds(new Set());
            setPriceOverrides(new Map());
          },
        },
      );
    },
    [batchUpdatePrices, t],
  );

  // Of the checkbox selection, how many are not among the currently loaded
  // rows — fed into the discount popover and removal dialog's hidden-count
  // line (row-selection spec: confirmations state items outside the current
  // results).
  const selectionHiddenCount = React.useMemo(() => {
    const shownIds = new Set(allRows.map((row) => row.id));
    let hidden = 0;
    for (const id of selectedIds) if (!shownIds.has(id)) hidden++;
    return hidden;
  }, [allRows, selectedIds]);

  // A hidden selected unit can only be resolved from the whole-offer fetch,
  // not the loaded page — while that fetch is still in flight, applying now
  // would silently skip the hidden units it's meant to cover (row-selection
  // spec), the same race `handleConfirmApplyDiscountToAll` below already
  // guards against for its own whole-offer path.
  const isResolvingHiddenSelection = selectionHiddenCount > 0 && allUnitsQuery.data === undefined;

  const handleApplyDiscountToSelected = React.useCallback(
    (discountPercent: number) => {
      if (isResolvingHiddenSelection) return;
      // Safe to fall back to the loaded page here: this path only runs when
      // isResolvingHiddenSelection is false, meaning either the whole-offer
      // fetch already resolved, or there's no hidden unit for it to miss.
      const source = allUnitsQuery.data ?? allRowsRef.current;
      const rows = source.filter((row) => selectedIds.has(row.id));
      handleApplyDiscount(rows, discountPercent);
    },
    [allUnitsQuery.data, handleApplyDiscount, isResolvingHiddenSelection, selectedIds],
  );

  const handleRequestApplyDiscountToAll = React.useCallback((discountPercent: number) => {
    setPendingConfirmAllDiscount(discountPercent);
  }, []);

  const handleConfirmApplyDiscountToAll = React.useCallback(() => {
    if (pendingConfirmAllDiscount === null || allUnitsQuery.data === undefined) return;
    handleApplyDiscount(allUnitsQuery.data, pendingConfirmAllDiscount);
    setPendingConfirmAllDiscount(null);
  }, [allUnitsQuery.data, handleApplyDiscount, pendingConfirmAllDiscount]);

  const handleCancelApplyDiscountToAll = React.useCallback(() => {
    setPendingConfirmAllDiscount(null);
  }, []);

  const clearAllFilters = React.useCallback(() => {
    onFilterChange(EMPTY_FILTER_VALUE);
    onSearchChange('');
    onAddressChange('');
    onRangeChange({});
  }, [onFilterChange, onSearchChange, onAddressChange, onRangeChange]);

  const { filterFields, filterPanelLabels, panelFilterValue, handlePanelFiltersChange } =
    useUnitsTabFilters(
      offerId,
      filterValue,
      onFilterChange,
      address,
      onAddressChange,
      rangeValue,
      onRangeChange,
      t,
      tMap,
      canViewCosts,
      canViewRent,
      canViewSupplierDetails,
    );

  const { sentinelRef } = useScrollSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  const columns = React.useMemo(
    () =>
      buildUnitsTabColumns({
        canEdit,
        selectedIds,
        onCheckedChange,
        onNoteBlur,
        canViewSupplierDetails,
        price: { overrides: priceOverrides, onPriceChange: handlePriceChange, canEdit },
        t,
      }),
    [
      canEdit,
      selectedIds,
      onCheckedChange,
      onNoteBlur,
      canViewSupplierDetails,
      priceOverrides,
      handlePriceChange,
      t,
    ],
  );

  // Column visibility/order are user-configurable via the shared ColumnPicker,
  // persisted under the merged table's own 'ooh:offer-table:*' storage
  // namespace — fully independent of the catalog's own 'ooh:inventory:*'
  // preferences and of Decyzje klienta's 'ooh:offer-decisions:*' preferences.
  // Seeded from the user's Katalog nośników layout on first visit — a plain
  // synchronous call so it runs before `useOfferTableColumnPreferences`'s own
  // lazy `useState` initializer reads storage (see the hook's own doc comment).
  const allColumnIds = React.useMemo(() => columns.map((c) => c.id), [columns]);
  const catalogFieldIds = React.useMemo(
    () => getOfferTableCatalogColumnIds(canViewSupplierDetails, t),
    [canViewSupplierDetails, t],
  );
  seedOfferTableColumnPreferencesFromCatalog(catalogFieldIds);
  const {
    visibleColumnIds,
    hiddenColumnIds,
    toggleColumn,
    columnOrder,
    reorderColumn,
    moveColumnUp: moveColumnPickerUp,
    moveColumnDown: moveColumnPickerDown,
    columnWidths,
    setColumnWidth,
  } = useOfferTableColumnPreferences(allColumnIds);

  // A retired `?tab=pricing` address reveals the price columns for this visit
  // only, without altering the user's saved layout (offer-unified-table: "A
  // retired address reveals the columns it was saved for").
  const effectiveVisibleColumnIds = React.useMemo(() => {
    if (revealSurface !== 'pricing') return visibleColumnIds;
    const next = new Set(visibleColumnIds);
    for (const id of REVEALED_PRICE_COLUMN_IDS) next.add(id);
    return next;
  }, [revealSurface, visibleColumnIds]);

  // All columns sorted by the user-defined order (structural columns pinned first
  // when present). Used by the "Kolumny" picker so its list order matches the table.
  // Columns filtered by user-driven visibility, in user-defined order, with any
  // persisted resized width applied — passed to the actual table render.
  const { orderedColumns, visibleColumns } = React.useMemo(
    () =>
      buildOrderedVisibleColumns<(typeof columns)[number]>(
        columns,
        columnOrder,
        effectiveVisibleColumnIds,
        columnWidths,
      ),
    [columns, columnOrder, effectiveVisibleColumnIds, columnWidths],
  );

  // Must be declared before early returns to satisfy react-hooks/rules-of-hooks.
  // Sourced from the whole-offer all-units fetch (not the paginated/filtered table
  // rows) so bulk download covers EVERY offer unit. Ignores row selection per D-7.
  // u.mediaUnitId is the media unit PK; u.id is the offer_units join-table row ID.
  const allOfferUnitIds = React.useMemo(() => allUnits.map((u) => u.mediaUnitId), [allUnits]);

  // Availability period columns (offer-unified-table, CU-86cbc3r8b) — requested
  // only for `allRows` (the rows currently loaded/filtered/sorted on screen),
  // never the whole offer. Appended after the persisted visible columns,
  // outside the column-preference/order system entirely (matches the
  // catalog's own `buildRichAvailabilityColumns` wiring).
  const availability = useOfferTableAvailability({
    offerId,
    offerLabel: offerLabel ?? `#${offerId}`,
    canEdit,
    rows: allRows,
    defaultDateFrom: defaultDateFrom ? defaultDateFrom.toISOString().slice(0, 10) : null,
    defaultDateTo: defaultDateTo ? defaultDateTo.toISOString().slice(0, 10) : null,
    onOpenCampaignDatesEditor,
    forceShow: revealSurface === 'availability',
  });
  const finalColumns = React.useMemo(
    () => [...visibleColumns, ...availability.columns],
    [visibleColumns, availability.columns],
  );

  const handleHeaderSort = React.useCallback(
    (key: string) => {
      const nextKey = key as SortKey;
      const nextDir: SortDir = nextKey === activeSortKey && sortDir === 'asc' ? 'desc' : 'asc';
      onSortChange?.(nextKey, nextDir);
    },
    [activeSortKey, onSortChange, sortDir],
  );

  // Sort-reset chip (offer-table-chrome rework): sorting moved to column
  // headers, but the default `sortOrder` key ("Kolejność dodania") has no
  // backing column — shown only while a REAL column sort is active. The chip
  // label is looked up from the sorted column's own `label` so it never
  // drifts from what the header itself says.
  const activeSortColumnLabel = React.useMemo(
    () => columns.find((c) => c.sortKey === activeSortKey)?.label,
    [columns, activeSortKey],
  );
  const activeSortInfo =
    activeSortKey !== 'sortOrder' && activeSortColumnLabel
      ? { columnLabel: activeSortColumnLabel, dir: sortDir }
      : undefined;
  const handleClearSort = React.useCallback(() => {
    onSortChange?.('sortOrder', 'asc');
  }, [onSortChange]);

  // Legend entries for the availability sub-toolbar — shared with the
  // catalog's own AvailabilitySubToolbar usage (see import comment above).
  const availabilityLegendItems = React.useMemo(
    () => buildAvailabilityLegendItems(tCommon),
    [tCommon],
  );

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">{t('workspace.unitsTab.loading')}</div>
    );
  }

  if (isError) {
    return <div className="p-6 text-sm text-destructive">{t('workspace.unitsTab.error')}</div>;
  }

  const unitCount = totalCount;
  // Only surface the grand total when a filter narrows the view — when
  // unfiltered, `unitCount` already IS the grand total.
  const grandTotal = hasActiveFilter ? grandTotalQuery.totalCount : undefined;
  // The offer's true unfiltered total — used by the no-results state and the
  // bulk-discount confirmation, which must always state the whole-offer count.
  const unfilteredTotal = hasActiveFilter ? (grandTotal ?? unitCount) : unitCount;
  const rowsWithExistingDiscount = allUnits.filter((row) => (row.discountPercent ?? 0) > 0).length;

  return (
    <div>
      {/* Sticky controls block (toolbar/search/filters/map toggle/availability
          strip), pinned below the workspace tabs bar (top-11 there == h-11
          here) so scrolling the page — including while the pointer is over
          the table below — scrolls the whole page (and this block into its
          pinned position) instead of being swallowed by a nested scroll
          container. See workspace-shell.tsx for the matching top-0 tabs-bar
          sticky. */}
      <div className="sticky top-11 z-10 bg-background" ref={toolbarRef}>
        <UnitsTabToolbar
          canEdit={canEdit}
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder={t('workspace.unitsTab.filterFields.searchPlaceholder')}
          searchAriaLabel={t('workspace.unitsTab.filterFields.searchAriaLabel')}
          fields={filterFields}
          value={panelFilterValue}
          onChange={handlePanelFiltersChange}
          labels={filterPanelLabels}
          rangeLabels={filterPanelLabels.rangeLabels}
          onOpenAddDrawer={handleAddUnitsFromCatalog}
          onOpenMapMode={canEdit ? handleAddUnitsFromMap : undefined}
          columnPickerColumns={orderedColumns}
          hiddenColumnIds={hiddenColumnIds}
          onToggleColumn={toggleColumn}
          onMoveColumnUp={moveColumnPickerUp}
          onMoveColumnDown={moveColumnPickerDown}
          grandTotal={grandTotal}
          unitCount={unitCount}
          isMapVisible={isMapVisible}
          onToggleMap={() => setIsMapVisible((value) => !value)}
          mapTooltip={t('workspace.unitsTab.map.toggle')}
          showAvailability={availability.showAvailability}
          onToggleAvailability={availability.toggleAvailability}
          availabilityTooltip={t('workspace.availabilityTab.toggleLabel')}
          activeSort={activeSortInfo}
          onClearSort={handleClearSort}
          discountAction={
            <BulkDiscountPopover
              canEdit={canEdit}
              selectedCount={selectedIds.size}
              hiddenCount={selectionHiddenCount}
              applySelectedDisabled={isResolvingHiddenSelection}
              onApplySelected={handleApplyDiscountToSelected}
              onApplyAll={handleRequestApplyDiscountToAll}
              t={t}
            />
          }
          bulkDownloadAction={
            onBulkDownloadSpecs ? (
              <OfferBulkDownloadTrigger
                unitIds={allOfferUnitIds}
                canDownloadSpecs
                start={async (ids) => onBulkDownloadSpecs(ids)}
                confirmDownload={confirmBulkDownload ?? (async () => {})}
                cancelDownload={cancelBulkDownload ?? (() => {})}
                pendingManifest={bulkDownloadPendingManifest ?? null}
                iconOnly
              />
            ) : null
          }
        />

        {isMapVisible ? (
          <UnitsTabMap
            units={allUnits}
            onUnitClick={handleMapUnitClick}
            t={t}
            className="h-72 border-b border-border"
          />
        ) : null}

        {/* Slim availability strip — same placement as the catalog's
            AvailabilitySubToolbar: its own row directly above the table, so
            toggling availability never reflows the toolbar above it. The
            toggle itself lives in the dense action row above (offer-units-availability-toggle). */}
        {availability.showAvailability ? (
          <AvailabilitySubToolbar
            granularity={availability.granularity}
            onGranularityChange={availability.setGranularity}
            legendItems={availabilityLegendItems}
            granularityTestId="offer-units-availability-granularity"
          />
        ) : null}
        {availability.showAvailability && availability.isError ? (
          <span
            className="border-b border-border/40 px-4 py-1 text-xs text-destructive"
            data-testid="offer-units-availability-error"
          >
            {t('workspace.availabilityTab.error')}
          </span>
        ) : null}

        {availability.showsNoDatesExplanation ? (
          <div
            className="flex flex-wrap items-center gap-2 border-b border-border/40 bg-muted/30 px-4 py-2 text-xs text-muted-foreground"
            data-testid="offer-units-availability-no-dates"
          >
            <span>{t('workspace.availabilityTab.noDefaultDateRange')}</span>
            {canEdit && onOpenCampaignDatesEditor ? (
              <Button
                size="xs"
                type="button"
                variant="outline"
                data-testid="offer-units-availability-no-dates-cta"
                onClick={onOpenCampaignDatesEditor}
              >
                {t('workspace.availabilityTab.setDefaultDateRangeCta')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* TooltipProvider is scoped tightly to just the table (not the whole
          page), mirroring the catalog's own scoped wrapper — see
          routes/media-units/index.tsx. Any occupied availability cell
          carrying an owner hint renders a Radix Tooltip, which throws
          without an ancestor TooltipProvider. */}
      <TooltipProvider delayDuration={150}>
        <div className="px-0">
          {renderUnitsTabContent({
            canEdit,
            displayItems: allRows,
            columns: finalColumns,
            onColumnReorder: reorderColumn,
            onColumnResizeEnd: setColumnWidth,
            sortBy: activeSortKey,
            sortDir,
            onSort: handleHeaderSort,
            onOpenMapMode: canEdit ? handleAddUnitsFromMap : undefined,
            onPrimaryAction: handleAddUnitsFromCatalog,
            offerId: String(offerId),
            hasActiveFilter,
            unfilteredTotal,
            onClearFilters: clearAllFilters,
            onScrollContainerElement: setTableScrollElement,
            stickyHeaderOffsetPx: tableStickyHeaderOffsetPx,
            t,
          })}
          {hasNextPage ? (
            <div
              ref={sentinelRef}
              data-testid="units-tab-scroll-sentinel"
              aria-hidden="true"
              className="h-px w-full"
            />
          ) : null}
          {isFetchingNextPage ? (
            <div className="p-3 text-center text-xs text-muted-foreground">
              {t('workspace.unitsTab.loading')}
            </div>
          ) : null}
        </div>
      </TooltipProvider>

      {/* Horizontal scrollbar, pinned directly above the totals footer. The
          table's own native one lives at the bottom of its full-height box —
          only reachable at the very end of the list, where this footer covers
          it. Offsetting by the measured footer height keeps the two apart. */}
      <StickyHorizontalScrollbar
        target={tableScrollElement}
        bottomOffset={footerHeight}
        ariaLabel={t('workspace.unitsTab.horizontalScrollbarAriaLabel')}
        data-testid="units-tab-horizontal-scrollbar"
      />

      <div className="sticky bottom-0 z-10" ref={footerRef}>
        <PricingTabTotalsFooter offerId={offerId} />
      </div>

      {canEdit ? (
        <BulkActionsOverlay
          t={t}
          selectedCount={selectedIds.size}
          reservedCount={
            Array.from(selectedIds).filter((id) => availability.reservedUnitIds.has(id)).length
          }
          hiddenCount={selectionHiddenCount}
          removeConfirmOpen={removeConfirmOpen}
          onClearSelection={clearSelection}
          onOpenRemoveConfirm={() => setRemoveConfirmOpen(true)}
          onCloseRemoveConfirm={() => setRemoveConfirmOpen(false)}
          onConfirmRemove={confirmRemove}
        />
      ) : null}

      {pendingConfirmAllDiscount !== null ? (
        <BulkDiscountConfirmDialog
          rowCount={unfilteredTotal}
          discountPercent={pendingConfirmAllDiscount}
          rowsWithExistingDiscount={rowsWithExistingDiscount}
          isFiltered={hasActiveFilter}
          disabled={allUnitsQuery.isLoading}
          onConfirm={handleConfirmApplyDiscountToAll}
          onCancel={handleCancelApplyDiscountToAll}
          t={t}
        />
      ) : null}

      {availability.multiSelection && availability.multiSelection.cells.length > 0 ? (
        <UnifiedSelectPopover
          selection={availability.multiSelection}
          resolveCell={availability.resolveMultiSelectCell}
          anchorRef={availability.multiSelectAnchorRef}
          offerScope={{ offerId, offerName: offerLabel ?? '' }}
          resolveMediaUnitLabel={(mediaUnitId) =>
            allRows.find((u) => u.mediaUnitId === mediaUnitId)?.identifier
          }
          onClear={availability.clearMultiSelection}
          onBatchReserveSuccess={availability.handleBatchReserveSuccess}
          onBatchReleaseSuccess={availability.handleBatchReleaseSuccess}
          queueJoinSection={availability.queueJoinSection}
          queueJoinLoading={availability.queueJoinLoading}
        />
      ) : null}
    </div>
  );
}
